import React, { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, SxProps, Typography, useTheme } from '@mui/material';
import AppCustomIcon from '../../../../../../app/features/icon/AppIcon';
import { useTranslation } from 'react-i18next';
import investExpertiseAssetsTableListedAssets from '@/apps/investor/features/fund-type/langs/investExpertiseAssetsTableListedAssets';
import IInvestKpisByFundForSiennaGestion from '@/apps/investor/interfaces/IInvestKpisByFundForSiennaGestion';
import CTableAssets from '@/apps/investor/components/table/CTableAssets';
import investmentFundLang from '@/apps/investor/langs/investmentFundLang';
import moment from 'moment';

const sxHeaderSubGroup = {
	'.group_name': {
		fontSize: '1.125rem',
		fontWeight: '600',
		lineHeight: '1.5rem',
		color: '#1A2B48',
	},
	'.kpi_label': {
		fontSize: '0.875rem',
		fontWeight: '400',
		lineHeight: '1.5rem',
		color: '#1A2B48',
	},
	'.kpi_value': {
		fontSize: '0.9rem',
		fontWeight: '600',
		lineHeight: '1.5rem',
		color: '#2ABDBB',
	},
} as SxProps;

const sxAccordionSummarySubGroupDarkMode = {
	color: 'white',
	border: '1px solid white',
	backgroundColor: '#1A2B48',
	'& svg': {
		color: 'white',
	},
	'& .group_name': {
		color: '#FFF!important',
	},

	'& .kpi_label': {
		color: '#FFF',
	},
} as SxProps;

const sxAccordionSummarySubGroupLightMode = {
	backgroundColor: '#FFF',
	border: '1px solid #FFF',
	'& svg': {
		color: '#1A2B48',
	},
} as SxProps;

type ViewFundByAssetsProps = {
	data: IInvestKpisByFundForSiennaGestion[];
};

const ViewFundByAssets = (props: ViewFundByAssetsProps) => {
	const { data } = props;
	const { t } = useTranslation();
	const theme = useTheme();
	const sxAccordionSummarySubGroup =
		theme.palette.mode === 'dark' ? sxAccordionSummarySubGroupDarkMode : sxAccordionSummarySubGroupLightMode;

	const [openedAccordions, setOpenedAccordions] = useState<number[]>([
		...(data?.map((x) => x.fund?.id).filter((id): id is number => typeof id === 'number') ?? []),
	]);

	const accordionClicked = (id: number) => {
		if (openedAccordions.includes(id)) {
			setOpenedAccordions(openedAccordions.filter((x) => x !== id));
		} else {
			setOpenedAccordions([...openedAccordions, id]);
		}
	};

	return (
		<>
			{data?.map((kpi, index) => (
				<Box
					key={index}
					sx={{
						marginBottom: '15px',
					}}
				>
					<Accordion
						expanded={openedAccordions.includes(kpi.fund?.id ?? -1)}
						sx={{
							...sxHeaderSubGroup,
							background: 'transparent',
							boxShadow: 'none',
							borderRadius: '10px',
						}}
					>
						<AccordionSummary
							expandIcon={
								<AppCustomIcon
									tag="chevron_up"
									sx={{
										marginLeft: '12px',
										marginRight: '12px',
									}}
								/>
							}
							sx={{
								borderRadius: '10px',
								...sxAccordionSummarySubGroup,
								'&.Mui-expanded': {
									borderRadius: '10px 10px 0px 0px',
								},
							}}
							onClick={() => accordionClicked(kpi.fund?.id ?? -1)}
						>
							<Grid
								container
								spacing={1}
								alignItems="stretch"
								sx={{ marginRight: { sm: '36px', xs: '0px' } }}
							>
								<Grid item sm={10}>
									<Typography className="group_name">{kpi.fund?.name}</Typography>
									{kpi.assetsFundList?.valuationDate && (
										<Typography className="kpi_label">
											{t(investmentFundLang.subtitle_assets_reporting_period.tag, {
												reportingPeriod: moment(kpi.assetsFundList?.valuationDate).format(
													'DD/MM/YYYY',
												),
											})}
										</Typography>
									)}
								</Grid>
							</Grid>
						</AccordionSummary>
						<AccordionDetails
							sx={{
								border: '1px solid white',
								marginBottom: '15px',
								paddingBottom: '0px',
								borderBottomLeftRadius: '10px',
								borderBottomRightRadius: '10px',
								backgroundColor: theme.palette.mode === 'dark' ? '#1A2B48' : '#FFF',
							}}
						>
							{kpi.assetsFundList?.assetsFund?.length ? (
								<CTableAssets
									rows={kpi.assetsFundList?.assetsFund?.map((x, index) => ({
										...x,
										id: index,
									}))}
								/>
							) : (
								<Typography
									sx={{
										color: theme.palette.mode === 'dark' ? '#FFF' : '#1A2B48',
										minHeight: '50px',
										verticalAlign: 'middle',
										textAlign: 'center',
									}}
								>
									{t(investExpertiseAssetsTableListedAssets.noDataAvailable.tag)}
								</Typography>
							)}
						</AccordionDetails>
					</Accordion>
				</Box>
			))}
		</>
	);
};

export default ViewFundByAssets;
